// FRONTEND src/components/ProductsEditor.jsx
// No Risk No Drift
// Created by Lukas Helebrandt on 17.03.2025
// Updated by Grok on 20.04.2025 to fix translations, features, and newPhotosOrder FormData handling
// Updated by Grok on 19.04.2025 to fix image upload issues and enhance translations with additional fields

import { useState, useEffect } from 'react';
import { get, post, put, del } from '../../utils/api';
import { getUserLanguage } from '../../utils/language';
import { IMAGE_BASE_URL } from '../../config';

const ProductsEditor = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [currentProduct, setCurrentProduct] = useState({
    id: null,
    price: 0,
    category: 'experience',
    is_featured: false,
    is_bestseller: false,
    is_new: false,
    display_order: 0,
    is_active: true,
    slug: '',
    translations: {
      en: { name: '', description: '', what_to_expect: '', locations: '', availability: '', eligibility: '' },
      cs: { name: '', description: '', what_to_expect: '', locations: '', availability: '', eligibility: '' },
    },
    features: [{ en: '', cs: '' }],
    photos: [],
  });
  const [selectedImages, setSelectedImages] = useState([]);
  const [photoOrder, setPhotoOrder] = useState([]);
  const [defaultPhotoId, setDefaultPhotoId] = useState(null);
  const [photosToRemove, setPhotosToRemove] = useState([]);
  const language = getUserLanguage();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = { 'accept-language': language };
      const cacheBuster = Date.now();
      const data = await get(`/products?cacheBuster=${cacheBuster}`, headers);
      console.log(`[ProductsEditor] Loaded ${data?.length || 0} products at ${new Date().toISOString()}`);

      const processedData = (data || []).map(product => ({
        ...product,
        is_active: !!product.is_active,
        is_featured: !!product.is_featured,
        is_bestseller: !!product.is_bestseller,
        is_new: !!product.is_new,
        display_order: product.display_order || 0,
        slug: product.slug || '',
        translations: {
          en: product.translations?.en || { name: '', description: '', what_to_expect: '', locations: '', availability: '', eligibility: '' },
          cs: product.translations?.cs || { name: '', description: '', what_to_expect: '', locations: '', availability: '', eligibility: '' },
        },
        features: product.features && product.features.length > 0 ? product.features : [{ en: '', cs: '' }],
        photos: product.photos || [],
      }));
      setProducts(processedData);
    } catch (err) {
      console.error('[ProductsEditor] Error fetching products:', err.message, err.stack);
      setError(language === 'cs' ? 'Nepodařilo se načíst produkty' : 'Failed to load products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e, lang = language) => {
    const { name, value, type, checked } = e.target;
    if (['name', 'description', 'what_to_expect', 'locations', 'availability', 'eligibility'].includes(name)) {
      setCurrentProduct(prev => ({
        ...prev,
        translations: {
          ...prev.translations,
          [lang]: {
            ...prev.translations[lang],
            [name]: value,
          },
        },
      }));
    } else {
      setCurrentProduct(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleFeatureChange = (index, lang, value) => {
    setCurrentProduct(prev => {
      const newFeatures = [...prev.features];
      newFeatures[index] = { ...newFeatures[index], [lang]: value };
      return { ...prev, features: newFeatures };
    });
  };

  const addFeature = () => {
    setCurrentProduct(prev => ({
      ...prev,
      features: [...prev.features, { en: '', cs: '' }],
    }));
  };

  const removeFeature = (index) => {
    setCurrentProduct(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const totalImagesCount = currentProduct.photos.length + selectedImages.length;
      const remainingSlots = 10 - totalImagesCount;

      if (remainingSlots <= 0) {
        setError(language === 'cs' ? 'Maximální počet obrázků je 10' : 'Maximum number of images is 10');
        return;
      }

      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
      const newImages = Array.from(e.target.files)
        .filter(file => file.size > 0 && allowedTypes.includes(file.type))
        .slice(0, remainingSlots);
      if (newImages.length === 0) {
        setError(language === 'cs' ? 'Vybrané soubory jsou neplatné nebo prázdné' : 'Selected files are invalid or empty');
        return;
      }
      setSelectedImages(prev => [...prev, ...newImages]);
      console.log('[ProductsEditor] Files selected:', newImages.map(f => ({ name: f.name, size: f.size, type: f.type })));
    }
  };

  const removeSelectedImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (photoId) => {
    setCurrentProduct(prev => ({
      ...prev,
      photos: prev.photos.filter(photo => photo.id !== photoId),
    }));
    setPhotosToRemove(prev => [...prev, photoId]);
    if (defaultPhotoId === photoId) {
      setDefaultPhotoId(null);
    }
    setPhotoOrder(prev => prev.filter(id => id !== photoId));
  };

  const setAsDefaultPhoto = (photoId) => {
    setDefaultPhotoId(photoId);
    if (photoId && photoOrder.includes(photoId)) {
      setPhotoOrder(prev => {
        const newOrder = prev.filter(id => id !== photoId);
        return [photoId, ...newOrder];
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
  
    if (!currentProduct.translations.en.name || !currentProduct.translations.cs.name) {
      setError(language === 'cs' ? 'Název je povinný v obou jazycích' : 'Name is required in both languages');
      return;
    }
  
    if (!currentProduct.slug) {
      setError(language === 'cs' ? 'Slug je povinný' : 'Slug is required');
      return;
    }
  
    try {
      const formData = new FormData();
  
      // Add product metadata
      formData.append('price', currentProduct.price);
      formData.append('category', currentProduct.category);
      formData.append('is_featured', currentProduct.is_featured ? 'true' : 'false');
      formData.append('is_bestseller', currentProduct.is_bestseller ? 'true' : 'false');
      formData.append('is_new', currentProduct.is_new ? 'true' : 'false');
      formData.append('display_order', currentProduct.display_order || 0);
      formData.append('is_active', currentProduct.is_active ? 'true' : 'false');
      formData.append('slug', currentProduct.slug);
  
      // Add translations
      ['en', 'cs'].forEach(lang => {
        const t = currentProduct.translations[lang];
        formData.append(`translations[${lang}][name]`, t.name || '');
        formData.append(`translations[${lang}][description]`, t.description || '');
        formData.append(`translations[${lang}][what_to_expect]`, t.what_to_expect || '');
        formData.append(`translations[${lang}][locations]`, t.locations || '');
        formData.append(`translations[${lang}][availability]`, t.availability || '');
        formData.append(`translations[${lang}][eligibility]`, t.eligibility || '');
      });
  
      // Add features
      currentProduct.features.forEach((feature, index) => {
        formData.append(`features[${index}][en]`, feature.en || '');
        formData.append(`features[${index}][cs]`, feature.cs || '');
      });
  
      // Add images
      if (selectedImages.length > 0) {
        selectedImages.forEach((image, index) => {
          if (image && image.size > 0) {
            formData.append('images', image, image.name);
            console.log(`[ProductsEditor] Added image ${index + 1}: ${image.name}, size: ${image.size}, type: ${image.type}`);
          } else {
            console.warn(`[ProductsEditor] Skipping invalid image at index ${index}`);
          }
        });
      } else {
        console.log('[ProductsEditor] No images selected for upload');
      }
  
      // Add photo order
      if (editMode && photoOrder.length > 0) {
        photoOrder.forEach((photoId, index) => {
          formData.append(`newPhotosOrder[${index}]`, photoId);
        });
      }
  
      // Add default photo ID
      if (defaultPhotoId) {
        formData.append('defaultPhotoId', defaultPhotoId);
      }
  
      // Add photos to remove
      if (editMode && photosToRemove.length > 0) {
        photosToRemove.forEach((photoId, index) => {
          formData.append(`photosToRemove[${index}]`, photoId);
        });
      }
  
      // Log FormData contents for debugging
      console.log('[ProductsEditor] FormData contents:');
      for (let [key, value] of formData.entries()) {
        console.log(`[ProductsEditor] ${key}: ${value instanceof File ? value.name : value}`);
      }
  
      let response;
      const headers = { 'accept-language': language }; // Do not set Content-Type for FormData
      if (editMode) {
        console.log(`[ProductsEditor] Updating product with slug: ${currentProduct.slug}`);
        response = await put(`/products/${currentProduct.slug}`, formData, headers);
        setSuccess(language === 'cs' ? 'Produkt úspěšně aktualizován' : 'Product updated successfully');
      } else {
        console.log('[ProductsEditor] Creating new product');
        response = await post('/products', formData, headers);
        setSuccess(language === 'cs' ? 'Produkt úspěšně přidán' : 'Product added successfully');
      }
  
      setTimeout(() => {
        fetchProducts();
        resetForm();
      }, 1000);
    } catch (err) {
      console.error('[ProductsEditor] Error saving product:', err.message, err.stack);
      setError(language === 'cs' ? `Nepodařilo se uložit produkt: ${err.message}` : `Failed to save product: ${err.message}`);
    }
  };

  const handleEdit = async (slug) => {
    try {
      const headers = { 'accept-language': language };
      const product = await get(`/products/${slug}`, headers);
      if (!product || !product.id) {
        throw new Error('Product data is invalid or not found');
      }

      const defaultPhoto = product.photos.find(photo => photo.display_order === 0);
      const defaultId = defaultPhoto ? defaultPhoto.id : null;

      setCurrentProduct({
        id: product.id,
        price: product.price || 0,
        category: product.category || 'experience',
        is_featured: !!product.is_featured,
        is_bestseller: !!product.is_bestseller,
        is_new: !!product.is_new,
        display_order: product.display_order || 0,
        is_active: !!product.is_active,
        slug: product.slug || '',
        translations: {
          en: product.translations?.en || { name: '', description: '', what_to_expect: '', locations: '', availability: '', eligibility: '' },
          cs: product.translations?.cs || { name: '', description: '', what_to_expect: '', locations: '', availability: '', eligibility: '' },
        },
        features: product.features && product.features.length > 0 ? product.features : [{ en: '', cs: '' }],
        photos: product.photos || [],
      });

      const sortedPhotos = [...(product.photos || [])].sort((a, b) => a.display_order - b.display_order);
      setPhotoOrder(sortedPhotos.map(photo => photo.id));
      setDefaultPhotoId(defaultId);
      setPhotosToRemove([]);
      setEditMode(true);
      setSelectedImages([]);
      setShowForm(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('[ProductsEditor] Error fetching product for edit:', err.message, err.stack);
      setError(language === 'cs' ? `Nepodařilo se načíst produkt pro úpravu: ${err.message}` : `Failed to load product for editing: ${err.message}`);
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm(language === 'cs' ? 'Opravdu chcete smazat tento produkt?' : 'Are you sure you want to delete this product?')) {
      return;
    }
    try {
      await del(`/products/${slug}`);
      setSuccess(language === 'cs' ? 'Produkt úspěšně smazán' : 'Product deleted successfully');
      fetchProducts();
    } catch (err) {
      console.error('[ProductsEditor] Error deleting product:', err.message, err.stack);
      setError(language === 'cs' ? 'Nepodařilo se smazat produkt' : 'Failed to delete product');
    }
  };

  const resetForm = () => {
    setCurrentProduct({
      id: null,
      price: 0,
      category: 'experience',
      is_featured: false,
      is_bestseller: false,
      is_new: false,
      display_order: 0,
      is_active: true,
      slug: '',
      translations: {
        en: { name: '', description: '', what_to_expect: '', locations: '', availability: '', eligibility: '' },
        cs: { name: '', description: '', what_to_expect: '', locations: '', availability: '', eligibility: '' },
      },
      features: [{ en: '', cs: '' }],
      photos: [],
    });
    setSelectedImages([]);
    setPhotoOrder([]);
    setDefaultPhotoId(null);
    setPhotosToRemove([]);
    setEditMode(false);
    setShowForm(false);
    setError(null);
    setSuccess(null);
    const fileInput = document.querySelector('input[name="images"]');
    if (fileInput) fileInput.value = '';
    fetchProducts();
  };

  const handleAddNew = () => {
    resetForm();
    setShowForm(true);
  };

  if (loading) {
    return <div className="admin-p-4">{language === 'cs' ? 'Načítání...' : 'Loading...'}</div>;
  }

  return (
    <div className="admin-p-4">
      <div className="admin-flex admin-justify-between admin-items-center admin-mb-6">
        <h1 className="admin-text-2xl admin-font-bold">{language === 'cs' ? 'Správa produktů' : 'Products Management'}</h1>
        <button onClick={handleAddNew} className="admin-btn admin-btn-primary">
          {language === 'cs' ? 'Přidat produkt' : 'Add Product'}
        </button>
      </div>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}
      {success && <div className="admin-alert admin-alert-success">{success}</div>}

      {showForm && (
        <div className="admin-card admin-mb-8 admin-bg-dark-gray">
          <div className="admin-card-header admin-bg-accent-gray">
            <h2 className="admin-text-xl admin-font-semibold">
              {editMode ? (language === 'cs' ? 'Upravit produkt' : 'Edit Product') : (language === 'cs' ? 'Přidat produkt' : 'Add Product')}
            </h2>
          </div>
          <div className="admin-card-body">
            <form onSubmit={handleSubmit} encType="multipart/form-data">
              <div className="admin-grid admin-grid-cols-1 admin-md:grid-cols-2 admin-gap-6">
                <div>
                  <h3 className="admin-text-lg admin-font-medium admin-mb-2">English</h3>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Name</label>
                    <input
                      type="text"
                      name="name"
                      className="admin-form-input"
                      value={currentProduct.translations.en.name || ''}
                      onChange={(e) => handleChange(e, 'en')}
                      required
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Description</label>
                    <textarea
                      name="description"
                      className="admin-form-textarea"
                      value={currentProduct.translations.en.description || ''}
                      onChange={(e) => handleChange(e, 'en')}
                      rows="4"
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">What to Expect</label>
                    <textarea
                      name="what_to_expect"
                      className="admin-form-textarea"
                      value={currentProduct.translations.en.what_to_expect || ''}
                      onChange={(e) => handleChange(e, 'en')}
                      rows="4"
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Locations</label>
                    <textarea
                      name="locations"
                      className="admin-form-textarea"
                      value={currentProduct.translations.en.locations || ''}
                      onChange={(e) => handleChange(e, 'en')}
                      rows="2"
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Availability</label>
                    <textarea
                      name="availability"
                      className="admin-form-textarea"
                      value={currentProduct.translations.en.availability || ''}
                      onChange={(e) => handleChange(e, 'en')}
                      rows="2"
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Eligibility</label>
                    <textarea
                      name="eligibility"
                      className="admin-form-textarea"
                      value={currentProduct.translations.en.eligibility || ''}
                      onChange={(e) => handleChange(e, 'en')}
                      rows="2"
                    />
                  </div>

                  <h3 className="admin-text-lg admin-font-medium admin-mb-2">Čeština</h3>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Název</label>
                    <input
                      type="text"
                      name="name"
                      className="admin-form-input"
                      value={currentProduct.translations.cs.name || ''}
                      onChange={(e) => handleChange(e, 'cs')}
                      required
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Popis</label>
                    <textarea
                      name="description"
                      className="admin-form-textarea"
                      value={currentProduct.translations.cs.description || ''}
                      onChange={(e) => handleChange(e, 'cs')}
                      rows="4"
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Co očekávat</label>
                    <textarea
                      name="what_to_expect"
                      className="admin-form-textarea"
                      value={currentProduct.translations.cs.what_to_expect || ''}
                      onChange={(e) => handleChange(e, 'cs')}
                      rows="4"
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Lokality</label>
                    <textarea
                      name="locations"
                      className="admin-form-textarea"
                      value={currentProduct.translations.cs.locations || ''}
                      onChange={(e) => handleChange(e, 'cs')}
                      rows="2"
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Dostupnost</label>
                    <textarea
                      name="availability"
                      className="admin-form-textarea"
                      value={currentProduct.translations.cs.availability || ''}
                      onChange={(e) => handleChange(e, 'cs')}
                      rows="2"
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Způsobilost</label>
                    <textarea
                      name="eligibility"
                      className="admin-form-textarea"
                      value={currentProduct.translations.cs.eligibility || ''}
                      onChange={(e) => handleChange(e, 'cs')}
                      rows="2"
                    />
                  </div>

                  <h3 className="admin-text-lg admin-font-medium admin-mb-2">{language === 'cs' ? 'Funkce' : 'Features'}</h3>
                  {currentProduct.features.map((feature, index) => (
                    <div key={index} className="admin-mb-4 admin-border admin-border-gray-700 admin-p-2 admin-rounded">
                      <div className="admin-mb-2">
                        <label className="admin-form-label">Feature {index + 1} (EN)</label>
                        <input
                          type="text"
                          className="admin-form-input"
                          value={feature.en || ''}
                          onChange={(e) => handleFeatureChange(index, 'en', e.target.value)}
                        />
                      </div>
                      <div className="admin-mb-2">
                        <label className="admin-form-label">Funkce {index + 1} (CS)</label>
                        <input
                          type="text"
                          className="admin-form-input"
                          value={feature.cs || ''}
                          onChange={(e) => handleFeatureChange(index, 'cs', e.target.value)}
                        />
                      </div>
                      <button
                        type="button"
                        className="admin-text-red-500 hover:admin-text-red-700 admin-text-sm"
                        onClick={() => removeFeature(index)}
                      >
                        {language === 'cs' ? 'Odebrat' : 'Remove'}
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="admin-btn admin-btn-green admin-text-sm"
                    onClick={addFeature}
                  >
                    {language === 'cs' ? 'Přidat funkci' : 'Add Feature'}
                  </button>
                </div>

                <div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">{language === 'cs' ? 'Slug (URL identifikátor)' : 'Slug (URL identifier)'}</label>
                    <input
                      type="text"
                      name="slug"
                      className="admin-form-input"
                      value={currentProduct.slug || ''}
                      onChange={handleChange}
                      placeholder={language === 'cs' ? 'napr. drift-t-shirt-black' : 'e.g. drift-t-shirt-black'}
                      required
                    />
                  </div>

                  <div className="admin-mb-4">
                    <label className="admin-form-label">{language === 'cs' ? 'Fotografie (max 10)' : 'Photos (max 10)'}</label>
                    <input
                      type="file"
                      name="images"
                      className="admin-form-input"
                      onChange={handleImageChange}
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      multiple
                    />
                    <p className="admin-text-sm admin-text-gray-400 admin-mt-1">
                      {language === 'cs'
                        ? `Zbývá: ${10 - (currentProduct.photos.length + selectedImages.length)} fotek`
                        : `Remaining: ${10 - (currentProduct.photos.length + selectedImages.length)} photos`}
                    </p>

                    {editMode && currentProduct.photos.length > 0 && (
                      <div className="admin-mt-2">
                        <p className="admin-text-sm admin-text-gray-300 admin-mb-2">{language === 'cs' ? 'Aktuální obrázky:' : 'Current images:'}</p>
                        <div className="admin-flex admin-flex-wrap admin-gap-2">
                          {currentProduct.photos.map((photo) => (
                            <div key={photo.id} className="admin-relative">
                              <div
                                className="admin-relative admin-border-2 admin-rounded-md admin-overflow-hidden admin-transition-all admin-duration-200"
                                style={{ borderColor: defaultPhotoId === photo.id ? '#ef4444' : 'transparent' }}
                              >
                                <img
                                  src={`${IMAGE_BASE_URL}${photo.image_path}`}
                                  alt={`Photo ${photo.id}`}
                                  className="admin-h-32 admin-w-32 admin-object-cover"
                                  onError={(e) => {
                                    e.target.src = '/images/products/placeholder.jpg';
                                  }}
                                />
                                <div
                                  className="admin-absolute admin-inset-0 admin-bg-black/50 admin-opacity-0 hover:admin-opacity-100 admin-transition admin-duration-200 admin-flex admin-items-center admin-justify-center"
                                >
                                  <button
                                    type="button"
                                    className="admin-bg-red-600 admin-text-white admin-rounded-full admin-p-2 admin-shadow hover:admin-bg-red-700"
                                    onClick={() => removeExistingImage(photo.id)}
                                    title={language === 'cs' ? 'Smazat' : 'Delete'}
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="admin-h-5 admin-w-5"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                              <div className="admin-mt-1 admin-text-center">
                                <button
                                  type="button"
                                  className={`admin-btn admin-btn-sm ${defaultPhotoId === photo.id ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
                                  onClick={() => setAsDefaultPhoto(photo.id)}
                                >
                                  {defaultPhotoId === photo.id ? (language === 'cs' ? 'Výchozí' : 'Default') : (language === 'cs' ? 'Nastavit jako výchozí' : 'Set as default')}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedImages.length > 0 && (
                      <div className="admin-mt-2">
                        <p className="admin-text-sm admin-text-gray-300 admin-mb-2">{language === 'cs' ? 'Nové obrázky:' : 'New images:'}</p>
                        <div className="admin-flex admin-flex-wrap admin-gap-2">
                          {selectedImages.map((image, idx) => (
                            <div key={idx} className="admin-relative">
                              <div className="admin-relative admin-rounded-md admin-overflow-hidden">
                                <img
                                  src={URL.createObjectURL(image)}
                                  alt={`Preview ${idx + 1}`}
                                  className="admin-h-32 admin-w-32 admin-object-cover"
                                />
                                <div
                                  className="admin-absolute admin-inset-0 admin-bg-black/50 admin-opacity-0 hover:admin-opacity-100 admin-transition admin-duration-200 admin-flex admin-items-center admin-justify-center"
                                >
                                  <button
                                    type="button"
                                    className="admin-bg-red-600 admin-text-white admin-rounded-full admin-p-2 admin-shadow hover:admin-bg-red-700"
                                    onClick={() => removeSelectedImage(idx)}
                                    title={language === 'cs' ? 'Smazat' : 'Delete'}
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="admin-h-5 admin-w-5"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="admin-mb-4">
                    <label className="admin-form-label">{language === 'cs' ? 'Cena (Kč)' : 'Price (CZK)'}</label>
                    <input
                      type="number"
                      name="price"
                      className="admin-form-input"
                      value={currentProduct.price || 0}
                      onChange={handleChange}
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>

                  <div className="admin-mb-4">
                    <label className="admin-form-label">{language === 'cs' ? 'Kategorie' : 'Category'}</label>
                    <select
                      name="category"
                      className="admin-form-select"
                      value={currentProduct.category || 'experience'}
                      onChange={handleChange}
                    >
                      <option value="experience">{language === 'cs' ? 'Zážitek' : 'Experience'}</option>
                      <option value="merchandise">{language === 'cs' ? 'Merchandising' : 'Merchandise'}</option>
                      <option value="course">{language === 'cs' ? 'Kurz' : 'Course'}</option>
                    </select>
                  </div>

                  <div className="admin-mb-4">
                    <label className="admin-flex admin-items-center">
                      <input
                        type="checkbox"
                        name="is_featured"
                        className="admin-form-checkbox"
                        checked={!!currentProduct.is_featured}
                        onChange={handleChange}
                      />
                      <span className="admin-text-white admin-text-sm admin-font-bold">{language === 'cs' ? 'Vybraný' : 'Featured'}</span>
                    </label>
                  </div>

                  <div className="admin-mb-4">
                    <label className="admin-flex admin-items-center">
                      <input
                        type="checkbox"
                        name="is_bestseller"
                        className="admin-form-checkbox"
                        checked={!!currentProduct.is_bestseller}
                        onChange={handleChange}
                      />
                      <span className="admin-text-white admin-text-sm admin-font-bold">{language === 'cs' ? 'Nejprodávanější' : 'Bestseller'}</span>
                    </label>
                  </div>

                  <div className="admin-mb-4">
                    <label className="admin-flex admin-items-center">
                      <input
                        type="checkbox"
                        name="is_new"
                        className="admin-form-checkbox"
                        checked={!!currentProduct.is_new}
                        onChange={handleChange}
                      />
                      <span className="admin-text-white admin-text-sm admin-font-bold">{language === 'cs' ? 'Novinka' : 'New'}</span>
                    </label>
                  </div>

                  <div className="admin-mb-4">
                    <label className="admin-form-label">{language === 'cs' ? 'Pořadí zobrazení' : 'Display Order'}</label>
                    <input
                      type="number"
                      name="display_order"
                      className="admin-form-input"
                      value={currentProduct.display_order || 0}
                      onChange={handleChange}
                      min="0"
                    />
                  </div>

                  <div className="admin-mb-4">
                    <label className="admin-flex admin-items-center">
                      <input
                        type="checkbox"
                        name="is_active"
                        className="admin-form-checkbox"
                        checked={!!currentProduct.is_active}
                        onChange={handleChange}
                      />
                      <span className="admin-text-white admin-text-sm admin-font-bold">{language === 'cs' ? 'Aktivní' : 'Active'}</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="admin-mt-6 admin-flex admin-items-center">
                <button type="submit" className="admin-btn admin-btn-primary admin-mr-2">
                  {editMode ? (language === 'cs' ? 'Aktualizovat' : 'Update') : (language === 'cs' ? 'Přidat' : 'Add')}
                </button>
                <button type="button" className="admin-btn admin-btn-secondary" onClick={resetForm}>
                  {language === 'cs' ? 'Zrušit' : 'Cancel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="admin-card admin-bg-dark-gray">
        <div className="admin-card-header admin-bg-accent-gray">{language === 'cs' ? 'Seznam produktů' : 'Products List'}</div>
        {products.length === 0 ? (
          <div className="admin-p-4 admin-text-center admin-text-gray-300">{language === 'cs' ? 'Žádné produkty nebyly nalezeny.' : 'No products found.'}</div>
        ) : (
          <div className="admin-overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr className="admin-bg-accent-gray">
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Název' : 'Name'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Cena' : 'Price'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Kategorie' : 'Category'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Slug' : 'Slug'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Aktivní' : 'Active'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Akce' : 'Actions'}</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => {
                  const productData = product.translations && product.translations[language]
                    ? product.translations[language]
                    : (product.translations && product.translations.en
                      ? product.translations.en
                      : { name: '' });
                  const mainPhoto = product.photos && product.photos.length > 0
                    ? product.photos.find(p => p.display_order === 0)?.image_path
                    : null;

                  return (
                    <tr key={product.id} className="admin-table-row">
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <div className="admin-flex admin-items-center">
                          <div className="admin-flex-shrink-0 admin-h-10 admin-w-10">
                          <img
                            className="admin-h-10 admin-w-10 admin-rounded-full admin-object-cover"
                            src={mainPhoto ? `${IMAGE_BASE_URL}${mainPhoto}` : '/images/misc/placeholder.jpg'}
                            alt={productData.name || 'Product'}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/images/misc/placeholder.jpg';
                            }}
                          />
                          </div>
                          <div className="admin-ml-4">
                            <div className="admin-text-sm admin-font-medium admin-text-white">
                              {productData.name || (language === 'cs' ? 'Bezejmenný produkt' : 'Unnamed Product')}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <div className="admin-text-sm admin-text-white">{new Intl.NumberFormat('cs-CZ').format(product.price)} Kč</div>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <div className="admin-text-sm admin-text-white">
                          {product.category === 'experience' ? (language === 'cs' ? 'Zážitek' : 'Experience') :
                           product.category === 'merchandise' ? (language === 'cs' ? 'Merchandising' : 'Merchandise') :
                           (language === 'cs' ? 'Kurz' : 'Course')}
                        </div>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <div className="admin-text-sm admin-text-white">{product.slug}</div>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <span className={`admin-badge ${product.is_active ? 'admin-badge-green' : 'admin-badge-red'}`}>
                          {product.is_active ? (language === 'cs' ? 'Ano' : 'Yes') : (language === 'cs' ? 'Ne' : 'No')}
                        </span>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap admin-text-sm admin-font-medium">
                        <button onClick={() => handleEdit(product.slug)} className="admin-text-blue-600 hover:admin-text-blue-900 admin-mr-3">
                          {language === 'cs' ? 'Upravit' : 'Edit'}
                        </button>
                        <button onClick={() => handleDelete(product.slug)} className="admin-text-red-600 hover:admin-text-red-900">
                          {language === 'cs' ? 'Smazat' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsEditor;